/**
 * Button — tüm eylemlerin tek geçiş noktası.
 *
 * ─── DOKUNMA HEDEFİ 44 px ────────────────────────────────────────────────────
 * `md` ve `lg` boyları en az 44 px yüksekliktedir (WCAG 2.5.8). Saha ekibi
 * bunu telefonda, muhtemelen eldivenle kullanacak. `sm` yalnız yoğun
 * masaüstü tablolarında ve fareyle kullanılan yerlerde kullanılmalı.
 *
 * ─── YÜKLENİYOR DURUMU BİLEŞENİN İÇİNDE ──────────────────────────────────────
 * `loading` verildiğinde düğme kendini devre dışı bırakır ve göstergeyi
 * kendisi çizer. Her çağrı yerinde ayrı ayrı yazılsaydı bazıları unutulur ve
 * kullanıcı aynı isteği iki kez gönderirdi — "oluşturma kolay olsun"
 * isteğinin en sık bozulduğu yer burasıdır.
 */
import type { ButtonHTMLAttributes, ReactNode } from 'react';

import { cx } from './cx.ts';

type Variant = 'primary' | 'secondary' | 'ghost' | 'danger';
type Size = 'sm' | 'md' | 'lg';

const VARIANT: Record<Variant, string> = {
  primary: 'bg-brand-600 text-white hover:bg-brand-700 active:bg-brand-900',
  secondary: 'bg-surface text-ink border border-border hover:bg-surface-sunken',
  ghost: 'bg-transparent text-ink hover:bg-surface-sunken',
  danger: 'bg-danger text-white hover:brightness-90',
};

const SIZE: Record<Size, string> = {
  sm: 'h-9 px-3 text-sm gap-1.5',
  md: 'min-h-11 px-4 text-sm gap-2',
  lg: 'min-h-12 px-5 text-base gap-2',
};

export interface ButtonProps extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'className'> {
  readonly variant?: Variant;
  readonly size?: Size;
  readonly loading?: boolean;
  /** Genişliği doldurur — mobilde varsayılan tercih. */
  readonly block?: boolean;
  readonly icon?: ReactNode;
}

export function Button({
  variant = 'primary',
  size = 'md',
  loading = false,
  block = false,
  icon,
  children,
  disabled,
  type = 'button',
  ...rest
}: ButtonProps): React.JSX.Element {
  return (
    <button
      type={type}
      disabled={disabled === true || loading}
      // Ekran okuyucu için: görsel göstergenin metin karşılığı.
      aria-busy={loading || undefined}
      className={cx(
        'inline-flex items-center justify-center rounded-[var(--radius-control)] font-medium',
        'transition-colors disabled:cursor-not-allowed disabled:opacity-50',
        VARIANT[variant],
        SIZE[size],
        block && 'w-full',
      )}
      {...rest}
    >
      {loading ? <Spinner /> : icon}
      {children}
    </button>
  );
}

function Spinner(): React.JSX.Element {
  return (
    <span
      className="size-4 animate-spin rounded-full border-2 border-current border-t-transparent"
      aria-hidden="true"
    />
  );
}
